import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Leaf, Sparkles } from "lucide-react";
import { api } from "../api";

const GREETING = {
  role: "assistant",
  content: "Hi! I'm your British Supplements advisor 🌿 Tell me what you're after — better sleep, more energy, joint support, immunity, focus, or something for over-40 — and I'll point you to the right clean supplements.",
  recommended: [],
};

const SUGGESTIONS = ["Help me sleep better", "More energy & focus", "Support for over 40", "Joint & muscle recovery"];

const AdvisorChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("bs_advisor_sid") || null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, busy]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const r = await api.post("/advisor/chat", { session_id: sessionId, message: msg });
      if (r.data.session_id && r.data.session_id !== sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem("bs_advisor_sid", r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, recommended: r.data.recommended || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having a moment — please try again shortly.", recommended: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open supplement advisor"
        className="fixed bottom-5 right-5 z-[75] flex h-14 w-14 items-center justify-center rounded-full bg-[#2D6A4F] text-white shadow-[0_12px_30px_-8px_rgba(16,72,50,0.6)] transition hover:bg-[#1B4332] active:scale-95"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-[75] flex h-[70vh] max-h-[560px] w-[calc(100vw-2.5rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border border-[#e6f0ea] bg-white shadow-2xl">
          <div className="flex items-center gap-3 bg-[#1B4332] px-5 py-4 text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15"><Leaf size={18} className="text-[#95D5B2]" /></span>
            <div>
              <p className="font-display text-sm font-bold uppercase leading-none">Supplement Advisor</p>
              <p className="mt-1 flex items-center gap-1 text-[11px] text-[#95D5B2]"><Sparkles size={11} /> AI powered · Clean advice</p>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-[#f7fbf8] p-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#2D6A4F] text-white" : "bg-white text-[#34463c] border border-[#e6f0ea]"}`}>
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.recommended && m.recommended.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {m.recommended.map((p) => (
                        <Link key={p.slug} to={`/product/${p.slug}`} onClick={() => setOpen(false)}
                          className="flex items-center gap-2 rounded-xl border border-[#dceadf] bg-[#f0f7f2] p-2 transition hover:bg-[#e6f3ec]">
                          <img src={p.image} alt="" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" />
                          <span className="flex-1 text-xs font-semibold text-[#1B4332]">{p.name}</span>
                          <span className="font-display text-xs font-bold text-[#2D6A4F]">£{Number(p.price).toFixed(2)}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="rounded-2xl border border-[#e6f0ea] bg-white px-4 py-3"><Loader2 size={16} className="animate-spin text-[#2D6A4F]" /></div></div>
            )}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="rounded-full border border-[#cfe3d6] bg-white px-3 py-1.5 text-xs font-semibold text-[#2D6A4F] transition hover:bg-[#f0f7f2]">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-[#e6f0ea] bg-white p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your goals..."
              className="flex-1 rounded-full border border-[#dceadf] px-4 py-2.5 text-sm outline-none focus:border-[#52B788]" />
            <button type="submit" disabled={busy || !input.trim()} aria-label="Send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2D6A4F] text-white transition hover:bg-[#1B4332] disabled:opacity-50">
              <Send size={17} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AdvisorChat;
